import type { ApiResponse, PaginatedResponse, ApiError, RequestConfig } from '@/types'
import { useUiStore } from '@/stores/useUiStore'
import { isNativePlatform } from '@/platform/detection'
import {
  readNativeTokens,
  writeNativeTokens,
  clearNativeTokens,
} from '@/platform/secureTokenStorage'

// Base URL: prefer explicit env var, otherwise use auto-discovered backend port
const BASE_URL = import.meta.env.VITE_API_URL || `http://localhost:${__BACKEND_PORT__}/api/v1`

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

// Request interceptor type
type RequestInterceptor = (config: RequestInit) => RequestInit | Promise<RequestInit>

// Response interceptor type
type ResponseInterceptor = (response: Response) => Response | Promise<Response>

// Error interceptor type
type ErrorInterceptor = (error: ApiError) => ApiError | Promise<ApiError>

// Interceptor storage
const requestInterceptors: RequestInterceptor[] = []
const responseInterceptors: ResponseInterceptor[] = []
const errorInterceptors: ErrorInterceptor[] = []

// On native, tokens live in the OS Keychain/Keystore (secureTokenStorage.ts),
// which is async. This in-memory cache keeps tokenManager's API synchronous
// for its ~30 call sites: hydrateNativeTokenCache() fills it from secure
// storage once at startup (main.tsx, before anything renders), and every
// write below updates the cache synchronously while persisting in the
// background. Unused on web, where localStorage stays authoritative.
let nativeAccessToken: string | null = null
let nativeRefreshToken: string | null = null

/** Fills the native token cache from secure storage. No-op on web. Call once at startup, before the first tokenManager read. */
export async function hydrateNativeTokenCache(): Promise<void> {
  if (!isNativePlatform()) return
  const tokens = await readNativeTokens()
  nativeAccessToken = tokens.accessToken
  nativeRefreshToken = tokens.refreshToken
}

// Token management
export const tokenManager = {
  getAccessToken: (): string | null => {
    return isNativePlatform() ? nativeAccessToken : localStorage.getItem(ACCESS_TOKEN_KEY)
  },

  getRefreshToken: (): string | null => {
    return isNativePlatform() ? nativeRefreshToken : localStorage.getItem(REFRESH_TOKEN_KEY)
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    if (isNativePlatform()) {
      nativeAccessToken = accessToken
      nativeRefreshToken = refreshToken
      void writeNativeTokens(accessToken, refreshToken)
      return
    }
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },

  clearTokens: (): void => {
    if (isNativePlatform()) {
      nativeAccessToken = null
      nativeRefreshToken = null
      void clearNativeTokens()
      return
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },

  hasValidToken: (): boolean => {
    return isNativePlatform() ? !!nativeAccessToken : !!localStorage.getItem(ACCESS_TOKEN_KEY)
  },
}

// Single-flight access-token refresh. Concurrent callers (e.g. parallel upload
// chunks that all 401 when the token expires) share one in-flight request —
// refresh-token rotation makes duplicate refresh calls fail hard.
// Returns the new access token, or null if refresh is impossible/failed
// (no refresh token, network error, or the refresh token itself is expired).
// Does NOT log out on failure — callers decide (useAuthStore logs out).
let refreshInFlight: Promise<string | null> | null = null

export function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  const flight = (async (): Promise<string | null> => {
    try {
      const refreshToken = tokenManager.getRefreshToken()
      if (!refreshToken) return null
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      })
      if (!response.ok) return null
      // Backend returns flat snake_case: {access_token, refresh_token}
      const tokens = (await response.json()) as { access_token: string; refresh_token: string }
      tokenManager.setTokens(tokens.access_token, tokens.refresh_token)
      return tokens.access_token
    } catch {
      return null
    }
  })()
  refreshInFlight = flight
  // Clear on settle — but only if a newer flight hasn't replaced this one.
  // (An async IIFE with no await settles synchronously, so clearing from a
  // `finally` inside the IIFE would run BEFORE the assignment above and leak
  // a permanently cached promise.)
  void flight.finally(() => {
    if (refreshInFlight === flight) refreshInFlight = null
  })
  return flight
}

// Endpoint the single-flight refresh itself calls. `request()` must never
// treat a 401 from this endpoint as refreshable — `refreshAccessToken()`
// already bypasses `request()` via a bare `fetch` (so this can't happen in
// practice today), but the explicit guard makes the no-recursion property
// hold even if a future caller routes `/auth/refresh` through `request()`.
function isAuthRefreshEndpoint(endpoint: string): boolean {
  return endpoint.includes('/auth/refresh')
}

// Fires when a 401 survives a refresh-and-retry attempt (refresh failed, or
// came back null because there's no refresh token). `client.ts` cannot
// import `useAuthStore` directly — `useAuthStore.ts` already imports from
// `client.ts`, so a static import would create a cycle. AuthProvider
// registers `logout` here instead (issue #1576).
type UnauthorizedHandler = () => void
let unauthorizedHandler: UnauthorizedHandler | null = null

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler
}

/**
 * Run an authenticated raw `fetch`, refreshing the access token once on a 401
 * and retrying exactly once with the new token.
 *
 * For the surfaces that legitimately bypass `request()` — SSE `ReadableStream`
 * responses, `FormData` bodies whose multipart boundary the browser must set,
 * and blob downloads. `doFetch` MUST build its headers from the token it is
 * handed, never from a value captured outside, or the retry re-sends the stale
 * token. Pass `signal` when the caller owns an `AbortController` so an abort
 * during the refresh cancels the retry instead of firing a doomed request.
 *
 * Never hand this the `/auth/refresh` endpoint — that would recurse.
 */
export async function fetchWithAuthRetry(
  doFetch: (token: string | null) => Promise<Response>,
  signal?: AbortSignal
): Promise<Response> {
  const response = await doFetch(tokenManager.getAccessToken())
  if (response.status !== 401) return response

  const newToken = await refreshAccessToken()
  if (!newToken) {
    // Session is unrecoverable (no refresh token, or it is expired/revoked).
    // Surface the original 401 — its body is still unread — and let the
    // handler log out rather than retrying against a dead token.
    unauthorizedHandler?.()
    return response
  }
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError')
  }
  // Exactly one retry: a 401 here means refreshing cannot fix it, so the
  // response is returned as-is instead of looping.
  return doFetch(newToken)
}

// Interceptor management
export const interceptors = {
  request: {
    use: (interceptor: RequestInterceptor): void => {
      requestInterceptors.push(interceptor)
    },
    clear: (): void => {
      requestInterceptors.length = 0
    },
  },
  response: {
    use: (interceptor: ResponseInterceptor): void => {
      responseInterceptors.push(interceptor)
    },
    clear: (): void => {
      responseInterceptors.length = 0
    },
  },
  error: {
    use: (interceptor: ErrorInterceptor): void => {
      errorInterceptors.push(interceptor)
    },
    clear: (): void => {
      errorInterceptors.length = 0
    },
  },
}

// Default auth interceptor - injects Authorization header
const authInterceptor: RequestInterceptor = (config) => {
  const token = tokenManager.getAccessToken()
  if (token) {
    const headers = new Headers(config.headers)
    headers.set('Authorization', `Bearer ${token}`)
    return { ...config, headers }
  }
  return config
}

// Register default auth interceptor
interceptors.request.use(authInterceptor)

// 402 insufficient-credits interceptor — shows toast for non-chat endpoints.
// Chat send/receive goes through raw fetch (api/agent.ts, useHermesChat), which
// bypasses this interceptor entirely and surfaces its own error inline.
const insufficientCreditsInterceptor: ErrorInterceptor = (error) => {
  if (error.status === 402) {
    useUiStore.getState().showToast({
      type: 'warning',
      title: "You're out of credits",
      message: 'Upgrade your plan to continue creating content.',
      duration: 8000,
      onAction: () => {
        window.location.href = '/settings/plan#plan'
      },
      actionLabel: 'View Plans',
    })
  }
  return error
}
interceptors.error.use(insufficientCreditsInterceptor)

// Build URL with query parameters
function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const url = new URL(endpoint.startsWith('http') ? endpoint : `${BASE_URL}${endpoint}`)

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value))
      }
    })
  }

  return url.toString()
}

// Parse error response
async function parseErrorResponse(response: Response): Promise<ApiError> {
  let message = 'An unexpected error occurred'
  let code: string | undefined
  let details: Record<string, string[]> | undefined
  let body: Record<string, unknown> | undefined

  try {
    const data = await response.json()
    body = data
    // Handle multiple backend error formats:
    // - {"message": "..."} (generic)
    // - {"detail": "..."} (FastAPI/auth middleware 401s)
    // - {"error": {"code": "...", "message": "..."}} (error handler middleware)
    message = data.message || data.detail || data.error?.message || message
    code = data.code || data.error?.code
    details = data.details || data.error?.details
  } catch {
    // If response is not JSON, use status text
    message = response.statusText || message
  }

  return {
    message,
    code,
    status: response.status,
    details,
    body,
  }
}

// 429 auto-retry: a rate-limited request was never processed by the server,
// so retrying is safe for every method. Waits out Retry-After when the
// backend provides it, else exponential backoff.
const RATE_LIMIT_MAX_RETRIES = 2
const RATE_LIMIT_MAX_DELAY_MS = 15_000

function rateLimitDelayMs(response: Response, attempt: number): number {
  const retryAfter = Number(response.headers.get('Retry-After'))
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, RATE_LIMIT_MAX_DELAY_MS)
  }
  return Math.min(1000 * 2 ** attempt, RATE_LIMIT_MAX_DELAY_MS)
}

// Core request function
async function request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
  const { params, body, ...init } = config

  // Build base config
  let requestConfig: RequestInit = {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init.headers,
    },
  }

  // Add body if provided
  if (body !== undefined) {
    requestConfig.body = JSON.stringify(body)
  }

  // Apply request interceptors
  for (const interceptor of requestInterceptors) {
    requestConfig = await interceptor(requestConfig)
  }

  // Build URL with params
  const url = buildUrl(endpoint, params)

  // Make request — wrap to convert browser-native TypeError ("Failed to fetch")
  // into a structured ApiError so callers can distinguish a network failure
  // (DNS / CORS / backend down / Slow 3G) from a generation/server failure.
  let response!: Response
  let hasAttemptedRefresh = false
  for (let attempt = 0; ; attempt++) {
    try {
      response = await fetch(url, requestConfig)
    } catch (err) {
      // Preserve user-initiated cancellation (AbortController) — let it propagate
      // unchanged so callers checking `err.name === 'AbortError'` keep working.
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err
      }
      // Browser fetch raises a bare TypeError on network failure. Normalize it.
      const networkError: ApiError = {
        message: 'Network error — check your connection or try again',
        code: 'network_error',
        status: 0,
      }
      let normalized = networkError
      for (const interceptor of errorInterceptors) {
        normalized = await interceptor(normalized)
      }
      throw normalized
    }

    if (response.status === 429 && attempt < RATE_LIMIT_MAX_RETRIES) {
      // Rate limited: the server rejected without processing, so retry after
      // the backoff instead of surfacing a permanent-looking failure.
      if (attempt === 0) {
        useUiStore.getState().showToast({
          type: 'info',
          title: 'Too many requests',
          message: 'Retrying automatically…',
          duration: 4000,
        })
      }
      await new Promise((resolve) => setTimeout(resolve, rateLimitDelayMs(response, attempt)))
      if (init.signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError')
      }
      continue
    }

    // 401: the access token expired mid-session (#1576). Refresh once via the
    // single-flight helper and retry the original request exactly once — a
    // second 401 after a successful refresh means the token is being
    // rejected for a reason refreshing cannot fix, so it is surfaced instead
    // of looping.
    if (response.status === 401 && !hasAttemptedRefresh && !isAuthRefreshEndpoint(endpoint)) {
      hasAttemptedRefresh = true
      const newToken = await refreshAccessToken()
      if (newToken) {
        // Rebuild the request from scratch and re-run request interceptors so
        // `authInterceptor` re-reads the new token from localStorage — never
        // hand-patch the Authorization header directly.
        let retryConfig: RequestInit = {
          ...init,
          headers: {
            'Content-Type': 'application/json',
            ...init.headers,
          },
        }
        if (body !== undefined) {
          retryConfig.body = JSON.stringify(body)
        }
        for (const interceptor of requestInterceptors) {
          retryConfig = await interceptor(retryConfig)
        }
        requestConfig = retryConfig
        if (init.signal?.aborted) {
          throw new DOMException('The operation was aborted.', 'AbortError')
        }
        continue
      }
      // Refresh is impossible or failed outright (no refresh token, network
      // error, or the refresh token itself is expired/revoked) — this
      // session cannot recover. Log out cleanly instead of leaving pollers
      // hammering a dead token forever; the caller's normal error handling
      // below still surfaces the 401.
      unauthorizedHandler?.()
    }

    break
  }

  // Apply response interceptors
  for (const interceptor of responseInterceptors) {
    response = await interceptor(response)
  }

  // Handle error responses
  if (!response.ok) {
    let error = await parseErrorResponse(response)

    // Apply error interceptors
    for (const interceptor of errorInterceptors) {
      error = await interceptor(error)
    }

    throw error
  }

  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return undefined as T
  }

  // Parse JSON response.
  // For non-paginated wrapped responses { items: [...] } (without limit/offset),
  // return the items array directly so stores expecting arrays work.
  // Paginated responses (legacy {items, total, limit, offset} or new {data, meta})
  // are left intact for normalizePaginatedResponse() in getList() to handle.
  const json = await response.json()
  if (json && typeof json === 'object' && !Array.isArray(json)) {
    const obj = json as Record<string, unknown>
    // New envelope: {data: [...], meta: {...}} — leave intact for getList()
    if ('data' in obj && 'meta' in obj && Array.isArray(obj.data) && typeof obj.meta === 'object') {
      return json as T
    }
    // Legacy wrapped: {items: [...]} without pagination — unwrap to array
    if ('items' in obj && Array.isArray(obj.items) && !('limit' in obj && 'offset' in obj)) {
      return obj.items as T
    }
  }
  return json as T
}

// Legacy backend paginated response shape (items/limit/offset format)
interface BackendPaginatedResponse<T> {
  items: T[]
  total: number
  limit: number
  offset: number
}

// New standard envelope: {data: [...], meta: {total, limit, offset, has_more}}
interface BackendEnvelopeResponse<T> {
  data: T[]
  meta: { total: number; limit: number; offset: number; has_more: boolean }
}

// Type guard to detect legacy backend paginated format
function isBackendPaginated<T>(raw: unknown): raw is BackendPaginatedResponse<T> {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    'items' in raw &&
    'limit' in raw &&
    'offset' in raw &&
    Array.isArray((raw as BackendPaginatedResponse<T>).items)
  )
}

// Type guard to detect new standard envelope format
function isBackendEnvelope<T>(raw: unknown): raw is BackendEnvelopeResponse<T> {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    'data' in raw &&
    'meta' in raw &&
    Array.isArray((raw as BackendEnvelopeResponse<T>).data) &&
    typeof (raw as BackendEnvelopeResponse<T>).meta === 'object'
  )
}

// Normalize backend paginated response to frontend PaginatedResponse format.
// Handles: new envelope (data/meta), legacy format (items/limit/offset), and mock format (data/page/pageSize).
function normalizePaginatedResponse<T>(raw: unknown): PaginatedResponse<T> {
  // New standard envelope: {data, meta: {total, limit, offset, has_more}}
  if (isBackendEnvelope<T>(raw)) {
    const { data, meta } = raw
    const pageSize = meta.limit || 10
    return {
      data,
      total: meta.total,
      page: Math.floor(meta.offset / pageSize) + 1,
      pageSize,
      totalPages: Math.ceil(meta.total / pageSize),
    }
  }
  // Legacy format: {items, total, limit, offset}
  if (isBackendPaginated<T>(raw)) {
    const { items, total, limit, offset } = raw
    const pageSize = limit || 10
    return {
      data: items,
      total,
      page: Math.floor(offset / pageSize) + 1,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
    }
  }
  // Already in PaginatedResponse format (mock data or pre-normalized)
  return raw as PaginatedResponse<T>
}

// HTTP method helpers
export const api = {
  get: <T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> => {
    return request<ApiResponse<T>>(endpoint, { ...config, method: 'GET' })
  },

  getList: async <T>(endpoint: string, config?: RequestConfig): Promise<PaginatedResponse<T>> => {
    const raw = await request<unknown>(endpoint, { ...config, method: 'GET' })
    return normalizePaginatedResponse<T>(raw)
  },

  post: <T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> => {
    return request<ApiResponse<T>>(endpoint, { ...config, method: 'POST', body })
  },

  put: <T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> => {
    return request<ApiResponse<T>>(endpoint, { ...config, method: 'PUT', body })
  },

  patch: <T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> => {
    return request<ApiResponse<T>>(endpoint, { ...config, method: 'PATCH', body })
  },

  delete: <T = void>(
    endpoint: string,
    config?: RequestConfig
  ): Promise<T extends void ? void : ApiResponse<T>> => {
    return request(endpoint, { ...config, method: 'DELETE' })
  },
}

/** Request config acting on `businessId` rather than the active business (verified server-side). */
export function inBusiness(businessId: string): RequestConfig {
  return { headers: { 'X-Business-ID': businessId } }
}

// Type guard to check if an error is an ApiError.
// Network failures wrapped in `request()` arrive as { status: 0, code: 'network_error' }
// and pass this guard so per-feature catch blocks can branch on `.status` / `.code`.
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'status' in error &&
    typeof (error as ApiError).message === 'string' &&
    typeof (error as ApiError).status === 'number'
  )
}

// True only for the wrapped network-failure ApiError (browser TypeError → status 0).
export function isNetworkError(error: unknown): boolean {
  return isApiError(error) && (error.code === 'network_error' || error.status === 0)
}

// Render a short, human-friendly reason from any thrown value. Used by the
// wizard per-concept catch blocks so users see "network error" / "server error
// 500" / "timed out" instead of the raw browser `TypeError("Failed to fetch")`.
export function describeError(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled'
  if (isNetworkError(error)) return 'network error'
  if (isApiError(error)) {
    if (error.status >= 500) return `server error ${error.status}`
    if (error.status === 408) return 'timed out'
    if (error.status > 0) return `${error.message} (${error.status})`
    return error.message
  }
  if (error instanceof Error) {
    if (/timed? ?out/i.test(error.message)) return 'timed out'
    return error.message
  }
  return 'unknown error'
}

// Get configured base URL
export function getBaseUrl(): string {
  return BASE_URL
}

// Export default client
export default api
